/**
 * Curated Target Library for "Find the Image" Game
 * 
 * Each target is an emoji-based icon that gets overlaid onto the AI-generated scene.
 * The host picks a category and target, and the app places it at a random position.
 * This gives us exact bounding box coordinates without relying on AI detection.
 */

const TARGET_LIBRARY = [
  {
    category: 'Animals',
    icon: '🐾',
    targets: [
      { id: 'red-fox', name: 'Red Fox', emoji: '🦊', color: '#FF6B35' },
      { id: 'purple-butterfly', name: 'Purple Butterfly', emoji: '🦋', color: '#A855F7' },
      { id: 'golden-fish', name: 'Golden Fish', emoji: '🐠', color: '#F59E0B' },
      { id: 'green-frog', name: 'Green Frog', emoji: '🐸', color: '#22C55E' },
      { id: 'pink-flamingo', name: 'Pink Flamingo', emoji: '🦩', color: '#EC4899' },
      { id: 'blue-whale', name: 'Blue Whale', emoji: '🐋', color: '#3B82F6' },
    ]
  },
  {
    category: 'Objects',
    icon: '🎯',
    targets: [
      { id: 'golden-key', name: 'Golden Key', emoji: '🔑', color: '#EAB308' },
      { id: 'red-diamond', name: 'Red Diamond', emoji: '💎', color: '#EF4444' },
      { id: 'purple-crystal', name: 'Purple Crystal', emoji: '🔮', color: '#8B5CF6' },
      { id: 'green-clover', name: 'Lucky Clover', emoji: '🍀', color: '#16A34A' },
      { id: 'blue-compass', name: 'Blue Compass', emoji: '🧭', color: '#0EA5E9' },
      { id: 'orange-star', name: 'Orange Star', emoji: '⭐', color: '#F97316' },
    ]
  },
  {
    category: 'Food & Drink',
    icon: '🍕',
    targets: [
      { id: 'red-cherry', name: 'Red Cherry', emoji: '🍒', color: '#DC2626' },
      { id: 'yellow-banana', name: 'Yellow Banana', emoji: '🍌', color: '#FACC15' },
      { id: 'purple-grape', name: 'Purple Grapes', emoji: '🍇', color: '#7C3AED' },
      { id: 'green-apple', name: 'Green Apple', emoji: '🍏', color: '#4ADE80' },
      { id: 'orange-slice', name: 'Orange Slice', emoji: '🍊', color: '#FB923C' },
      { id: 'pink-donut', name: 'Pink Donut', emoji: '🍩', color: '#F472B6' },
    ]
  },
  {
    category: 'Travel',
    icon: '✈️',
    targets: [
      { id: 'red-balloon', name: 'Red Balloon', emoji: '🎈', color: '#EF4444' },
      { id: 'blue-rocket', name: 'Blue Rocket', emoji: '🚀', color: '#3B82F6' },
      { id: 'yellow-taxi', name: 'Yellow Taxi', emoji: '🚕', color: '#FDE047' },
      { id: 'green-bicycle', name: 'Green Bicycle', emoji: '🚲', color: '#22D3EE' },
      { id: 'purple-umbrella', name: 'Purple Umbrella', emoji: '☂️', color: '#A855F7' },
      { id: 'red-anchor', name: 'Red Anchor', emoji: '⚓', color: '#B91C1C' },
    ]
  },
  {
    category: 'Sports',
    icon: '⚽',
    targets: [
      { id: 'basketball', name: 'Basketball', emoji: '🏀', color: '#EA580C' },
      { id: 'soccer-ball', name: 'Soccer Ball', emoji: '⚽', color: '#FAFAFA' },
      { id: 'tennis-ball', name: 'Tennis Ball', emoji: '🎾', color: '#BEF264' },
      { id: 'gold-trophy', name: 'Gold Trophy', emoji: '🏆', color: '#F59E0B' },
      { id: 'red-boxing', name: 'Boxing Glove', emoji: '🥊', color: '#DC2626' },
      { id: 'blue-surfboard', name: 'Surfboard', emoji: '🏄', color: '#06B6D4' },
    ]
  },
  {
    category: 'Music & Art',
    icon: '🎨',
    targets: [
      { id: 'red-guitar', name: 'Red Guitar', emoji: '🎸', color: '#B91C1C' },
      { id: 'purple-note', name: 'Music Note', emoji: '🎵', color: '#7C3AED' },
      { id: 'yellow-trumpet', name: 'Trumpet', emoji: '🎺', color: '#EAB308' },
      { id: 'blue-paint', name: 'Paint Palette', emoji: '🎨', color: '#2563EB' },
      { id: 'green-drum', name: 'Drum', emoji: '🥁', color: '#059669' },
      { id: 'pink-mic', name: 'Microphone', emoji: '🎤', color: '#EC4899' },
    ]
  }
];

/**
 * Get all categories
 */
export const getCategories = () => TARGET_LIBRARY;

/**
 * Get targets for a specific category
 */
export const getTargetsByCategory = (categoryName) => {
  const cat = TARGET_LIBRARY.find(c => c.category === categoryName);
  return cat ? cat.targets : [];
};

/**
 * Get a specific target by ID
 */
export const getTargetById = (targetId) => {
  for (const cat of TARGET_LIBRARY) {
    const target = cat.targets.find(t => t.id === targetId);
    if (target) return target;
  }
  return null;
};

/**
 * Generate a random position for the target within the image.
 * Avoids edges (10% margin) and very center (too obvious).
 * Returns normalized coordinates (0-1 range).
 * 
 * @param {number} targetSize - Size of the target as fraction of image (0.03 = 3%)
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export const generateRandomPosition = (targetSize = 0.045) => {
  const margin = 0.1; // 10% margin from edges
  const centerExclude = 0.15; // Avoid dead center zone

  let x, y;
  let attempts = 0;

  do {
    x = margin + Math.random() * (1 - 2 * margin - targetSize);
    y = margin + Math.random() * (1 - 2 * margin - targetSize);
    attempts++;

    // Check if too close to center
    const centerX = x + targetSize / 2;
    const centerY = y + targetSize / 2;
    const distFromCenter = Math.sqrt(
      Math.pow(centerX - 0.5, 2) + Math.pow(centerY - 0.5, 2)
    );

    if (distFromCenter > centerExclude || attempts > 20) break;
  } while (true);

  return {
    x: Math.round(x * 1000) / 1000,
    y: Math.round(y * 1000) / 1000,
    width: targetSize,
    height: targetSize
  };
};

export default TARGET_LIBRARY;
